import { ReactComponent as IconCalendar } from "@common_assets/svg/calendar-clock-outlined.svg"
import { ReactComponent as IconMoney } from "@common_assets/svg/money.svg"
import { ReactComponent as IconStake } from "@common_assets/svg/stake.svg"
import { ReactComponent as IconUser } from "@common_assets/svg/user-solid.svg"
import {
  Box,
  Link,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { AxiosError, AxiosResponse } from "axios"
import { useAppSelector } from "hooks/store"
import { Trans, useTranslation } from "react-i18next"
import { UseQueryResult } from "react-query"
import { Cell, Pie, PieChart } from "recharts"
import { selectUser } from "store/auth/auth.selectors"
import { IDashboardKpi } from "ts/interfaces/AdminDashboard"
import { isTeacher } from "utils/roleCheck"

import { RAW_INTERPOLATION } from "./behaviorFormat"
import DashboardKpiCard from "./DashboardKpiCard"
import DashboardSectionBody from "./DashboardSectionBody"
import KpiTrend from "./KpiTrend"

const formatCount = (value: number) => value.toLocaleString("en-US")
const formatRatio = (value: number | null) =>
  value === null ? "—" : `${(value * 100).toFixed(1)}%`
const formatMoney = (value: number) =>
  Math.round(value).toLocaleString("en-US")

const GRID_AREAS = ["pos", "att", "te", "st", "tx", "earn"]

// Next to Positivity, people and attendance in thirds over transactions and
// earnings in halves, so six tracks. Below desktop the cards pair up.
const kpiGridSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: {
    mobile: "1fr 1fr",
    desktop: "288fr repeat(6, 148fr)",
  },
  gridTemplateRows: { desktop: "repeat(2, minmax(168px, 1fr))" },
  gridTemplateAreas: {
    mobile: "\"pos pos\" \"att te\" \"st tx\" \"earn earn\"",
    desktop:
      "\"pos att att te te st st\" \"pos tx tx tx earn earn earn\"",
  },
}

const KpiSkeleton = () => (
  <Box sx={kpiGridSx} aria-busy="true">
    {GRID_AREAS.map((area) => (
      <Skeleton
        key={area}
        variant="rounded"
        data-testid="kpi-card-skeleton"
        sx={{
          gridArea: area,
          height: "auto",
          minHeight: 168,
          borderRadius: "16px",
        }}
      />
    ))}
  </Box>
)

const PositivityDonut = ({ positivity }: { positivity: number | null }) => {
  const { palette } = useTheme()

  return (
    <Box aria-hidden sx={{ mb: 1 }}>
      <PieChart width={120} height={120}>
        <Pie
          data={
            positivity === null
              ? [{ value: 0 }, { value: 1 }]
              : [{ value: positivity }, { value: 1 - positivity }]
          }
          dataKey="value"
          innerRadius={40}
          outerRadius={58}
          startAngle={90}
          endAngle={-270}
          stroke="none"
          isAnimationActive={false}
        >
          <Cell fill={palette.mockup.success50} />
          <Cell
            fill={
              positivity === null
                ? palette.mockup.neutral90
                : palette.mockup.error50
            }
          />
        </Pie>
      </PieChart>
    </Box>
  )
}

const DonutLegend = ({
  bonuses,
  fines,
}: {
  bonuses: number
  fines: number
}) => {
  const { t } = useTranslation()

  return (
    <Stack direction="row" justifyContent="center" gap={2} mb={1}>
      {(
        [
          ["mockup.success50", "adminDashboard.legendBonuses", bonuses],
          ["mockup.error50", "adminDashboard.legendFines", fines],
        ] as const
      ).map(([dotColor, key, count]) => (
        <Stack key={key} direction="row" alignItems="center" gap={0.75}>
          <Box
            aria-hidden
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor }}
          />
          <Typography variant="body3" color="mockup.neutral30" fontWeight={500}>
            {t(key, { value: count.toLocaleString("en-US") })}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

const ATTENDANCE_EMAIL = "hello@classbank.com"

const AttendanceConnectLink = () => {
  const { t } = useTranslation()
  const user = useAppSelector(selectUser)?.user
  const teacher = user && isTeacher(user) ? user : undefined
  const values = {
    ...RAW_INTERPOLATION,
    schoolName: teacher?.school_name ?? "",
    schoolCity: teacher?.school_city ?? "",
    schoolId: teacher?.school_id ?? "",
    firstName: teacher?.first_name ?? "",
    lastName: teacher?.last_name ?? "",
    email: teacher?.email ?? "",
  }
  const subject = t("adminDashboard.attendanceEmailSubject", values)
  const body = t("adminDashboard.attendanceEmailBody", values)
  const href = `mailto:${ATTENDANCE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

  return (
    <Trans
      i18nKey="adminDashboard.attendanceConnectLink"
      components={{
        Link: (
          <Link
            href={href}
            color="primary.main"
            underline="always"
            sx={{
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "mockup.primary60",
                outlineOffset: "2px",
              },
            }}
          />
        ),
      }}
    />
  )
}

const KpiCards = ({ kpi }: { kpi: IDashboardKpi }) => {
  const { t } = useTranslation()

  // Compared on the displayed value (1 decimal), inclusive: "80.0%" is above.
  const shownPositivity =
    kpi.positivity === null ? null : (kpi.positivity * 100).toFixed(1)

  return (
    <Box sx={kpiGridSx}>
      <Box sx={{ gridArea: "pos" }}>
        <DashboardKpiCard
          accent="mockup.success50"
          valueColor="mockup.neutral0"
          value={shownPositivity === null ? "—" : `${shownPositivity}%`}
          label={t("adminDashboard.positivityRatio")}
          trend={<KpiTrend unit="pts" delta={kpi.positivity_trend} />}
          footer={
            shownPositivity !== null && (
              <Typography
                variant="body2"
                color="mockup.neutral30"
                fontWeight={500}
                sx={{ pt: 1.5 }}
              >
                {t(
                  Number(shownPositivity) >= 80
                    ? "adminDashboard.pbisAbove"
                    : "adminDashboard.pbisBelow",
                )}
              </Typography>
            )
          }
          sx={{ justifyContent: "center" }}
        >
          <PositivityDonut positivity={kpi.positivity} />
          <DonutLegend bonuses={kpi.total_bonuses} fines={kpi.total_fines} />
        </DashboardKpiCard>
      </Box>
      <Box sx={{ gridArea: "att" }}>
        <DashboardKpiCard
          accent="mockup.neutral40"
          icon={<IconCalendar width={20} height={20} />}
          iconColor="mockup.neutral40"
          value={formatRatio(kpi.attendance_ratio)}
          valueColor="mockup.neutral0"
          label={t("adminDashboard.attendanceRatio")}
          trend={<KpiTrend unit="pts" delta={kpi.attendance_ratio_trend} />}
          footer={
            kpi.attendance_ratio === null && (
              <Typography
                variant="body3"
                color="mockup.neutral30"
                fontWeight={500}
              >
                {kpi.has_attendance_integration ? (
                  t("adminDashboard.attendanceNoData")
                ) : (
                  <AttendanceConnectLink />
                )}
              </Typography>
            )
          }
        />
      </Box>
      <Box sx={{ gridArea: "te" }}>
        <DashboardKpiCard
          accent="mockup.primary60"
          icon={<IconUser width={20} height={20} />}
          iconBgColor="mockup.primary90"
          iconColor="mockup.primary60"
          value={formatCount(kpi.active_teachers)}
          label={t("adminDashboard.activeTeachers")}
          trend={<KpiTrend unit="count" delta={kpi.active_teachers_trend} />}
        />
      </Box>
      <Box sx={{ gridArea: "st" }}>
        <DashboardKpiCard
          accent="mockup.additional20"
          icon={<IconUser width={20} height={20} />}
          iconBgColor="mockup.additional90"
          iconColor="mockup.additional20"
          value={formatCount(kpi.active_students)}
          label={t("adminDashboard.activeStudents")}
          trend={<KpiTrend unit="count" delta={kpi.active_students_trend} />}
        />
      </Box>
      <Box sx={{ gridArea: "tx" }}>
        <DashboardKpiCard
          accent="mockup.neutral10"
          icon={<IconStake width={32} height={32} />}
          iconBgColor="mockup.primary95"
          iconColor="mockup.neutral10"
          value={formatCount(kpi.total_transactions)}
          label={t("adminDashboard.totalTransactions")}
          trend={<KpiTrend unit="count" delta={kpi.total_transactions_trend} />}
        />
      </Box>
      <Box sx={{ gridArea: "earn" }}>
        <DashboardKpiCard
          accent="mockup.secondary60"
          icon={<IconMoney width={32} height={32} />}
          iconBgColor="mockup.secondary90"
          iconColor="mockup.secondary60"
          value={formatMoney(kpi.total_earned)}
          label={t("adminDashboard.totalEarned")}
          trend={<KpiTrend unit="money" delta={kpi.total_earned_trend} />}
        />
      </Box>
    </Box>
  )
}

const KpiSummary = ({
  query,
}: {
  query: UseQueryResult<AxiosResponse<IDashboardKpi>, AxiosError>
}) => {
  const kpi = query.data?.data

  return (
    <DashboardSectionBody queries={[query]} skeleton={<KpiSkeleton />}>
      {kpi && <KpiCards kpi={kpi} />}
    </DashboardSectionBody>
  )
}

export default KpiSummary
